import {
    getGroupAdminRole,
    hasAtLeastRole,
    hasEventRole,
    hasUserGroup,
    isGroupAdmin,
} from '../../core/utils/permissions/permissions';
import { UserRole } from '@/app/shared/enums/UserRole';
import type { ValuesType } from '@fifa-bsm/bo-frontend-shared/utils/types';

interface User {
    is_admin: boolean;
    permissions: { name: string; has_permission: boolean }[];
}

type Group = 'guest_operations';
type Role = ValuesType<typeof UserRole>;

export class UserPermissionsService {
    private user: User;
    private mainEventRoles: any[];
    private eventRoles: any[];
    public readonly permissions: Permissions;

    constructor(user: User, mainEventRoles: any[], eventRoles: any[]) {
        this.user = user;
        this.mainEventRoles = mainEventRoles;
        this.eventRoles = eventRoles;
        this.permissions = this._buildPermissions();
    }

    _buildPermissions() {
        const userPermissionsMap: Map<string, boolean> = this.user.permissions.reduce(
            (map, permission) => {
                map.set(permission.name, permission.has_permission);
                return map;
            },
            new Map<string, boolean>()
        );

        const groupAndAtLeastRole = (group: Group, _role: Role) =>
            hasAtLeastRole(this.user, group, _role);
        const group = (_group: Group) => hasUserGroup(this.user, _group);
        const groupAdmin = (_group: Group) => isGroupAdmin(this.user, _group);

        const mainEventRole = (mainEventId: number, roles: Role | Role[]) =>
            hasEventRole(
                this.user,
                this.mainEventRoles,
                mainEventId,
                Array.isArray(roles) ? roles : [roles]
            );
        const mainEventAdmin = (mainEventId: number, _group: Group) =>
            mainEventRole(mainEventId, getGroupAdminRole(_group));
        const eventRole = (eventId: number, roles: Role | Role[]) =>
            hasEventRole(
                this.user,
                this.eventRoles,
                eventId,
                Array.isArray(roles) ? roles : [roles]
            );
        const isSuperAdmin = this.user.is_admin;
        const hasPermission = (permission: string) => !!userPermissionsMap.get(permission);

        return {
            events: {
                guestOperations: {
                    mainEvents: {
                        list: isSuperAdmin || group('guest_operations'),
                        addEdit:
                            isSuperAdmin ||
                            groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FPO),
                        configureEmailTemplate: (mainEventId) =>
                            isSuperAdmin ||
                            (group('guest_operations') &&
                                mainEventAdmin(mainEventId, 'guest_operations')),
                        addEditPDFTemplates: (mainEventId) =>
                            isSuperAdmin ||
                            (group('guest_operations') &&
                                mainEventAdmin(mainEventId, 'guest_operations')),
                        barcodes: (mainEventId) =>
                            isSuperAdmin ||
                            (group('guest_operations') &&
                                mainEventAdmin(mainEventId, 'guest_operations')),
                        vappConfiguration:
                            isSuperAdmin ||
                            hasPermission('guest-operations.events.vapp-distribution'),
                        exportBarcodes: (mainEventId: number) =>
                            isSuperAdmin ||
                            (groupAdmin('guest_operations') &&
                                mainEventRole(mainEventId, UserRole.GUEST_OPERATIONS_ADMIN) &&
                                hasPermission(
                                    'guest-operations.events.export-main-event-barcodes'
                                )),
                    },
                    events: {
                        list: isSuperAdmin || group('guest_operations'),
                        addEdit:
                            isSuperAdmin ||
                            groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FPO),
                        configureImport:
                            isSuperAdmin ||
                            hasPermission('guest-operations.events.restrict-import-to-blocks'),
                    },
                    users: {
                        listForMainEvent: (mainEventId) =>
                            isSuperAdmin || mainEventAdmin(mainEventId, 'guest_operations'),
                        listForEvent: (eventId) =>
                            isSuperAdmin || eventRole(eventId, UserRole.GUEST_OPERATIONS_ADMIN),
                    },
                },
                syncEventWithSecutix:
                    isSuperAdmin ||
                    (groupAdmin('guest_operations') &&
                        hasPermission('common.seat-plans.sync-with-secutix')),
            },
            seating: {
                guestOperations: {
                    mainEventList: isSuperAdmin || group('guest_operations'),
                    eventList: isSuperAdmin || group('guest_operations'),
                    picker: isSuperAdmin || group('guest_operations'),
                    deliveryStatuses: (mainEventId) =>
                        isSuperAdmin ||
                        (group('guest_operations') &&
                            mainEventRole(mainEventId, [UserRole.GUEST_OPERATIONS_ADMIN])),
                    changeSeatTagPlan:
                        isSuperAdmin ||
                        hasPermission('guest-operations.seat-plans.change-seat-tag-plan'),
                    eTicketing: {
                        changeDistributionTemplate:
                            isSuperAdmin ||
                            (groupAdmin('guest_operations') &&
                                hasPermission(
                                    'guest-operations.e-ticketing.distribution.change-templates'
                                )),
                        downloadTickets:
                            isSuperAdmin ||
                            (groupAdmin('guest_operations') &&
                                hasPermission(
                                    'guest-operations.e-ticketing.distribution.download-tickets'
                                )),
                    },
                    setProtocolHeartOnSeat: isSuperAdmin || groupAdmin('guest_operations'),
                    exportPhotoReport:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_FULL_VIEW
                        ),
                    exportFreeSeatsReport:
                        isSuperAdmin ||
                        groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FPO),
                    exportCancelledTickets:
                        isSuperAdmin ||
                        groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FPO),
                    exportQuickMatchReport:
                        isSuperAdmin ||
                        groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FPO),
                    matchLogs:
                        isSuperAdmin ||
                        groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_ADMIN),
                    lastMinuteAddition: (mainEventId) =>
                        isSuperAdmin || mainEventRole(mainEventId, UserRole.GUEST_OPERATIONS_FPO),
                    waitingList: (eventId) =>
                        isSuperAdmin || eventRole(eventId, UserRole.GUEST_OPERATIONS_FPO),
                },
                seatEditor:
                    isSuperAdmin ||
                    (groupAdmin('guest_operations') &&
                        hasPermission('common.seat-plans.run-position-designer')),
            },
            bookings: {
                guestOperations: {
                    mainEventsList:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),
                    mainEventParticipantsList:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_FULL_VIEW
                        ),
                    addMainEventParticipant:
                        isSuperAdmin ||
                        groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_ADMIN),
                    editMainEventParticipant:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),
                    addEditCollectionPoint:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),

                    participantsTickets:
                        isSuperAdmin ||
                        groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FPO),
                    addCompanion:
                        isSuperAdmin ||
                        groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_ADMIN),
                    ticketsDistribute:
                        isSuperAdmin ||
                        groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FPO),
                    assignCategory: (mainEventId) =>
                        isSuperAdmin || mainEventAdmin(mainEventId, 'guest_operations'),
                    manageCategories: (mainEventId) =>
                        isSuperAdmin || mainEventAdmin(mainEventId, 'guest_operations'),
                    assignParticipantIdentifier: (mainEventId) =>
                        isSuperAdmin || mainEventRole(mainEventId, UserRole.GUEST_OPERATIONS_FPO),
                    labelsPrint:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),
                    seatPlanSeatings:
                        isSuperAdmin ||
                        groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FPO),
                    addNewParticipant:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),
                    addEditCollectionPointGuestList:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),
                    participantsTicketsGuestList:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),
                    addNewParticipantGuestList:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),
                    updateCollectionPointGuestList:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),
                    capacityGuestList:
                        isSuperAdmin ||
                        groupAndAtLeastRole(
                            'guest_operations',
                            UserRole.GUEST_OPERATIONS_SUPPORT_STAFF
                        ),
                    updateSalesPortalFlag:
                        isSuperAdmin ||
                        hasPermission('guest_operations.guests.update-sales-portal-flag'),
                },
            },
            entities: {
                list: groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_ADMIN),
                addEdit: groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_ADMIN),
            },
            settings: {
                list: isSuperAdmin || group('guest_operations'),
                userManagement: {
                    list: isSuperAdmin || groupAdmin('guest_operations'),
                    managePermissions:
                        isSuperAdmin ||
                        hasPermission('common.user-management.manage-users-permissions'),
                    changePin: isSuperAdmin,
                },
                translations: {
                    list: isSuperAdmin,
                    edit: isSuperAdmin,
                },
                seatTags: groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_ADMIN),
                participantGroups: groupAndAtLeastRole(
                    'guest_operations',
                    UserRole.GUEST_OPERATIONS_ADMIN
                ),
            },
            guests: {
                updateSecutixContactNumber: (mainEventId) =>
                    isSuperAdmin || mainEventRole(mainEventId, UserRole.GUEST_OPERATIONS_FPO),
            },
            guestList: {
                mainEventsList:
                    isSuperAdmin ||
                    groupAndAtLeastRole(
                        'guest_operations',
                        UserRole.GUEST_OPERATIONS_SEATING_VIEW_ONLY
                    ),
                eventsList:
                    isSuperAdmin ||
                    groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FULL_VIEW),
                guestList:
                    isSuperAdmin ||
                    groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FULL_VIEW),
                bulkUpdateTicketAmounts: (mainEventId) =>
                    isSuperAdmin || mainEventRole(mainEventId, UserRole.GUEST_OPERATIONS_ADMIN),
                updateInconsistencies: (eventId) =>
                    isSuperAdmin || eventRole(eventId, [UserRole.GUEST_OPERATIONS_ADMIN]),
                groupsDashboard: (mainEventId) =>
                    isSuperAdmin || mainEventRole(mainEventId, UserRole.GUEST_OPERATIONS_FPO),
                editGroupDashboard: (mainEventId) =>
                    isSuperAdmin || mainEventAdmin(mainEventId, 'guest_operations'),
                exportGroupDashboard: (mainEventId) =>
                    isSuperAdmin || mainEventAdmin(mainEventId, 'guest_operations'),
            },
            vappDistribution:
                isSuperAdmin ||
                groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FPO),
            reports:
                isSuperAdmin ||
                groupAndAtLeastRole('guest_operations', UserRole.GUEST_OPERATIONS_FULL_VIEW),
            clubs: {
                list: isSuperAdmin,
                addEdit: isSuperAdmin,
            },
        } satisfies Permissions;
    }
}

export interface Permissions {
    events: {
        guestOperations: {
            mainEvents: {
                list: boolean;
                addEdit: boolean;
                configureEmailTemplate: (mainEventId: number) => boolean;
                addEditPDFTemplates: (mainEventId: number) => boolean;
                barcodes: (mainEventId: number) => boolean;
                vappConfiguration: boolean;
                exportBarcodes: (mainEventId: number) => boolean;
            };
            events: {
                list: boolean;
                addEdit: boolean;
                configureImport: boolean;
            };
            users: {
                listForMainEvent: (mainEventId: number) => boolean;
                listForEvent: (eventId: number) => boolean;
            };
        };
        syncEventWithSecutix: boolean;
    };
    seating: {
        guestOperations: {
            mainEventList: boolean;
            eventList: boolean;
            picker: boolean;
            deliveryStatuses: (mainEventId: number) => boolean;
            changeSeatTagPlan: boolean;
            eTicketing: {
                changeDistributionTemplate: boolean;
                downloadTickets: boolean;
            };
            setProtocolHeartOnSeat: boolean;
            exportPhotoReport: boolean;
            exportFreeSeatsReport: boolean;
            exportCancelledTickets: boolean;
            exportQuickMatchReport: boolean;
            matchLogs: boolean;
            lastMinuteAddition: (mainEventId: number) => boolean;
            waitingList: (eventId: number) => boolean;
        };
        seatEditor: boolean;
    };
    guests: {
        updateSecutixContactNumber: (mainEventId: number) => boolean;
    };
    bookings: {
        guestOperations: {
            mainEventsList: boolean;
            mainEventParticipantsList: boolean;
            addMainEventParticipant: boolean;
            editMainEventParticipant: boolean;
            addEditCollectionPoint: boolean;
            participantsTickets: boolean;
            addCompanion: boolean;
            ticketsDistribute: boolean;
            assignCategory: (mainEventId: number) => boolean;
            manageCategories: (mainEventId: number) => boolean;
            assignParticipantIdentifier: (mainEventId: number) => boolean;
            labelsPrint: boolean;
            seatPlanSeatings: boolean;
            addNewParticipant: boolean;
            addEditCollectionPointGuestList: boolean;
            participantsTicketsGuestList: boolean;
            addNewParticipantGuestList: boolean;
            updateCollectionPointGuestList: boolean;
            capacityGuestList: boolean;
            updateSalesPortalFlag: boolean;
        };
    };
    entities: {
        list: boolean;
        addEdit: boolean;
    };
    settings: {
        list: boolean;
        userManagement: {
            list: boolean;
            managePermissions: boolean;
            changePin: boolean;
        };
        translations: {
            list: boolean;
            edit: boolean;
        };
        seatTags: boolean;
        participantGroups: boolean;
    };
    guestList: {
        mainEventsList: boolean;
        eventsList: boolean;
        guestList: boolean;
        updateInconsistencies: (eventId: number) => boolean;
        groupsDashboard: (mainEventId: number) => boolean;
        editGroupDashboard: (mainEventId: number) => boolean;
        exportGroupDashboard(mainEventId: number): boolean;
        bulkUpdateTicketAmounts(mainEventId: number): boolean;
    };
    vappDistribution: boolean; // placeholder
    reports: boolean;
    clubs: {
        list: boolean;
        addEdit: boolean;
    };
}
